import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import type { NurseInShift } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";
import { useFetchScheduleWeeks } from "./useScheduleWeeks";

/**
 * A scheduled shift on one schedule day: by its id, or (older requests name only the nurse) by its nurse and shift
 * type, since a nurse can work both the Day and the Night shift of one day.
 */
export type ScheduledShiftQuery = { date: DateTime } & ({ shiftId: string } | { nurseId: number; shiftType: string });

const matches = (shift: Pick<NurseInShift, "id" | "nurseId" | "shiftType">, query: ScheduledShiftQuery): boolean =>
	"shiftId" in query ? shift.id === query.shiftId : shift.nurseId === query.nurseId && shift.shiftType === query.shiftType;

/**
 * Finds a scheduled shift for an action that isn't a panel on that day (approving a request from anywhere), in the
 * weeks `useFetchScheduleWeeks` reads, so with `staffing_schedule_cache` on a shift past the old list's weeks is found
 * too. With it off a shift id is looked up across every loaded week, as before. Resolves `undefined` when the shift
 * isn't scheduled; rejects when its week fails to load.
 */
export const useScheduledShiftLookup = (): ((query: ScheduledShiftQuery) => Promise<NurseInShift | undefined>) => {
	const { scheduledShiftsById } = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const fetchScheduleWeeks = useFetchScheduleWeeks();

	return useCallback(
		async (query: ScheduledShiftQuery) => {
			if (!isCacheEnabled && "shiftId" in query) return scheduledShiftsById.get(query.shiftId);
			const isoDate = toIsoDate(query.date);
			return (await fetchScheduleWeeks(query.date))
				.flatMap((week) => week.weeklyShifts)
				.flatMap((row) => row.days.filter((day) => toIsoDate(day.date) === isoDate))
				.flatMap((day) => day.nurses)
				.find((shift) => matches(shift, query));
		},
		[isCacheEnabled, scheduledShiftsById, fetchScheduleWeeks],
	);
};
